import { appData, tempConfigData } from "../../app/state/state.js";
import { checkDataUsage } from "./settingsApi.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    applyBrandingVariables,
    getContrastColor,
    normalizeHexColor,
} from "../../shared/ui/brandingTokens.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createTeamOptions } from "../../shared/ui/controls/controlOptions.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const showInactiveTypesByKey = new Map();
const FIELD_TYPE_OPTIONS = Object.freeze([
    { value: "text", label: "Short Text" },
    { value: "textarea", label: "Long Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "boolean", label: "Yes/No Toggle" },
    { value: "heading", label: "Heading" },
]);
const STREAM_SCOPE_OPTIONS_BY_KEY = Object.freeze({
    labTypes: Object.freeze([
        { value: "internal", label: "In-house" },
        { value: "external", label: "External" },
    ]),
    maintenanceTypes: Object.freeze([
        { value: "service", label: "Service" },
        { value: "issue", label: "Issue" },
    ]),
});
let pendingTypeNameFocus = null;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""));
}

function focusPendingTypeNameInput() {
    if (!pendingTypeNameFocus) {return;}
    const {key, id} = pendingTypeNameFocus;
    pendingTypeNameFocus = null;
    const index = tempConfigData?.[key]?.findIndex((item) => item?.id === id);
    if (index === undefined || index < 0) {return;}

    const input = document.querySelector(
        `[data-key="${CSS.escape(key)}"] input[data-index="${index}"][data-prop="name"]`,
    );
    if (!input) {return;}
    input.focus();
    input.select();
}

function requestTypeNameFocus(key, id) {
    pendingTypeNameFocus = {id, key};
    requestAnimationFrame(focusPendingTypeNameInput);
}

function getColorInputStyle(color) {
    const safeColor = normalizeHexColor(color, "#cccccc");
    return `background-color: ${safeColor}; color: ${getContrastColor(safeColor)};`;
}

function renderTypeSettingsCheckbox({
    checked = false,
    className = "",
    dataAttrs = "",
    label,
    title = "",
    value = "",
}) {
    const checkedAttr = checked ? " checked" : "";
    const titleAttr = title ? ` title="${attrValue(title)}"` : "";
    const valueAttr = value ? ` value="${attrValue(value)}"` : "";
    const classAttr = [NATIVE_CHECKBOX_CLASSES, className]
        .filter(Boolean)
        .join(" ");
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} settings-boolean-choice cursor-pointer" data-native-checkbox-label${titleAttr}>
            <input type="checkbox" class="${classAttr}"${valueAttr}${checkedAttr}${dataAttrs}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            ${label ? `<span class="text-xs text-gray-600">${escapeHTML(label)}</span>` : ""}
        </label>
    `;
}

function getSelectedStreams(key, item) {
    const streamOptions = STREAM_SCOPE_OPTIONS_BY_KEY[key] || [];
    if (streamOptions.length === 0) {return [];}
    if (Array.isArray(item?.streams) && item.streams.length > 0) {
        return item.streams.map(String);
    }
    return streamOptions.map((option) => option.value);
}

function renderTypeStreamScopeControls(key, index, value, disabled = false) {
    const streamOptions = STREAM_SCOPE_OPTIONS_BY_KEY[key] || [];
    if (streamOptions.length === 0) {return "";}

    const selectedStreams = getSelectedStreams(key, value);
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <div class="settings-config-flags" aria-label="Stream scope">
            ${streamOptions.map((option) => renderTypeSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                dataAttrs: ` data-index="${index}" data-prop="streams"${disabledAttr}`,
                label: option.label,
                title: `Available for ${option.label}`,
                value: option.value,
            })).join("")}
        </div>
    `;
}

function typeSupportsMaintenanceIssueStream(key, value) {
    return key === "maintenanceTypes" &&
        getSelectedStreams(key, value).includes("issue");
}

function renderMaintenanceTypeColumnHeaders() {
    return `
        <div class="settings-maintenance-type-header">
            <span>Type</span>
            <span class="text-center">Issue</span>
            <span class="text-center">Service</span>
            <span>Main Fields</span>
            <span>Repair Fields</span>
            <span class="text-center">Archive</span>
            <span class="text-center">Delete</span>
        </div>
    `;
}

function renderLabTypeColumnHeaders() {
    return `
        <div class="settings-lab-type-header">
            <span>Type</span>
            <span class="text-center">In-house</span>
            <span class="text-center">External</span>
            <span>Main Fields</span>
            <span class="text-center">Archive</span>
            <span class="text-center">Delete</span>
        </div>
    `;
}

function renderMaintenanceTypeStreamCells(key, index, value, disabled = false) {
    const selectedStreams = getSelectedStreams(key, value);
    const disabledAttr = disabled ? " disabled" : "";
    const streamOptions = [
        {value: "issue", label: "Issue"},
        {value: "service", label: "Service"},
    ];

    return streamOptions.map((option) => `
        <div class="settings-option-boolean-cell" data-mobile-label="${option.label}">
            ${renderTypeSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                dataAttrs: ` data-index="${index}" data-prop="streams"${disabledAttr}`,
                label: "",
                title: `Available for ${option.label}`,
                value: option.value,
            })}
        </div>
    `).join("");
}

function renderLabTypeStreamCells(key, index, value, disabled = false) {
    const selectedStreams = getSelectedStreams(key, value);
    const disabledAttr = disabled ? " disabled" : "";
    const streamOptions = [
        {value: "internal", label: "In-house"},
        {value: "external", label: "External"},
    ];

    return streamOptions.map((option) => `
        <div class="settings-option-boolean-cell" data-mobile-label="${option.label}">
            ${renderTypeSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                dataAttrs: ` data-index="${index}" data-prop="streams"${disabledAttr}`,
                label: "",
                title: `Available for ${option.label}`,
                value: option.value,
            })}
        </div>
    `).join("");
}

function renderMaintenanceFieldButton({
    key,
    index,
    fieldKey,
    label,
    count,
    disabled = false,
}) {
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="manage-fields-btn settings-maintenance-type-field-btn ${count > 0 ? "settings-maintenance-type-field-btn--active" : ""}" data-index="${index}" data-key="${key}" data-field-key="${fieldKey}"${disabledAttr}>
            <span>${label}${count > 0 ? ` (${count})` : ""}</span>
        </button>
    `;
}

function getSortedSharedTeams(includeHidden = true) {
    const sourceTeams = Array.isArray(appData.teams) ? appData.teams : [];
    const teams = includeHidden
        ? [...sourceTeams]
        : sourceTeams.filter((team) => team.visible !== false);

    teams.sort((a, b) => {
        const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 999;
        const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 999;
        if (orderA !== orderB) {return orderA - orderB;}
        return (a.name || "").localeCompare(b.name || "");
    });

    return teams;
}

function renderTypeOrderButton({key, index, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move up" : "Move down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="move-type-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-key="${attrValue(key)}" data-index="${index}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function moveTypeItem(key, index, direction) {
    const sourceItems = tempConfigData?.[key] || [];
    const targetItem = sourceItems[index];
    if (!targetItem || typeof targetItem !== "object") {return false;}

    const targetIsActive = targetItem.isActive !== false;
    const entries = sourceItems
        .map((item, itemIndex) => ({
            index: itemIndex,
            item,
            order: Number(item?.order ?? itemIndex + 1) || itemIndex + 1,
        }))
        .filter((entry) => (entry.item?.isActive !== false) === targetIsActive)
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
    const currentPosition = entries.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    const current = entries[currentPosition].item;
    const next = entries[nextPosition].item;
    const currentOrder = Number(current.order) || entries[currentPosition].order;
    const nextOrder = Number(next.order) || entries[nextPosition].order;
    current.order = nextOrder;
    next.order = currentOrder;
    return true;
}

function getSortedTypeValues(values, showInactive) {
    const safeValues = Array.isArray(values) ? [...values] : [];
    return safeValues
        .filter((value) => showInactive || value?.isActive !== false)
        .sort((a, b) => {
            const activeDiff = (a?.isActive === false ? 1 : 0) -
                (b?.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            return (a.order || 0) - (b.order || 0);
        });
}

function getMaxActiveTypeOrder(key) {
    return Math.max(
        0,
        ...(tempConfigData?.[key] || [])
            .filter((item) => item?.isActive !== false)
            .map((item) => Number(item.order) || 0),
    );
}

function renderTypeColorControl({key, index, color, disabled = false}) {
    const safeColor = attrValue(color || "#cccccc");
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <span class="settings-color-control" data-link-id="color-pair-${key}-${index}">
            <input type="text" value="${safeColor}" data-index="${index}" data-prop="color" data-role="text" class="settings-color-value" placeholder="#hex"${disabledAttr}>
            <input type="color" value="${safeColor}" data-index="${index}" data-prop="color" data-role="picker" class="settings-color-picker" aria-label="Pick colour"${disabledAttr}>
        </span>
    `;
}

function isFieldPlaceholderDisabled(field) {
    return ["heading", "boolean", "date"].includes(field?.type);
}

function isFieldRequiredAvailable(field) {
    return field?.type !== "heading";
}

function renderFieldOrderButton({direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move field up" : "Move field down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="settings-field-order-btn ${direction === "up" ? "move-up-field-btn" : "move-down-field-btn"}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function bindFieldEditorControls(fields, updateSaveButton) {
    const container = document.getElementById("fields-list-container");
    if (!container) {return;}

    bindNativeFieldBehavior(container);
    container.querySelectorAll("[data-field-type-control]").forEach((root) => {
        const index = parseInt(root.dataset.index, 10);
        const field = fields[index];
        if (!field) {return;}

        createSelectControl({
            root,
            accessibleName: "Field input type",
            value: field.type || "text",
            options: FIELD_TYPE_OPTIONS,
            portalMenu: true,
            onChange: (value) => {
                field.type = value || "text";
                updateSaveButton(true);
                refreshFieldsList(fields, updateSaveButton);
            },
        });
    });
}

/**
 * Mounts type-team scope checkbox multiselect controls.
 * @param {HTMLElement} container Rendered Settings tab container.
 * @returns {void}
 */
export function bindTypeSettingsControls(container) {
    bindNativeFieldBehavior(container);
    container.querySelectorAll(".settings-color-value").forEach((input) => {
        input.addEventListener("click", () => {
            const linkId = input.closest("[data-link-id]")?.dataset.linkId;
            const picker = linkId
                ? container.querySelector(
                    `[data-link-id="${linkId}"] [data-role="picker"]`,
                )
                : null;
            if (!picker) {return;}

            if (typeof picker.showPicker === "function") {
                try {
                    picker.showPicker();
                    return;
                } catch {
                    // Fall back to click for browsers that reject showPicker.
                }
            }
            picker.click();
        });
    });

    const teamOptions = createTeamOptions(getSortedSharedTeams(true), {
        includeHidden: true,
    });
    const allTeamIds = teamOptions.map((option) => String(option.value));
    container.querySelectorAll("[data-type-team-control]").forEach((root) => {
        const key = root.dataset.key;
        const index = parseInt(root.dataset.index, 10);
        const item = tempConfigData?.[key]?.[index];
        if (!item || typeof item !== "object") {return;}
        const isInactive = item.isActive === false;
        const selectedTeamIds = Array.isArray(item.teamIds)
            ? item.teamIds.map(String)
            : [];
        const selectedValue = selectedTeamIds.length === 0
            ? allTeamIds
            : selectedTeamIds;

        createSelectControl({
            root,
            accessibleName: "Type team scope",
            value: selectedValue,
            options: teamOptions,
            multiple: true,
            searchable: true,
            searchThreshold: 7,
            checkboxMenu: true,
            bulkSelection: true,
            emptySelectionLabel: "All teams",
            allSelectedLabel: "All teams",
            selectedCountLabel: "Selected teams",
            disabled: isInactive,
            placeholder: "Select teams",
            searchPlaceholder: "Search teams",
            onChange: (value) => {
                if (item.isActive === false) {return;}
                const nextValues = Array.isArray(value) ? value.map(String) : [];
                item.teamIds = nextValues.length === allTeamIds.length
                    ? []
                    : nextValues;
                root.dispatchEvent(new Event("input", { bubbles: true }));
            },
        });
    });
}

/**
 * Renders entity type rows with active flags, team scoping, and field controls.
 * @param {string} title Section title.
 * @param {string} key Config key.
 * @param {Array<object>} values Type config values.
 * @returns {string} Type editor HTML.
 */
export function renderTypeEditor(title, key, values) {
    const showInactive = showInactiveTypesByKey.get(key) === true;
    const visibleValues = getSortedTypeValues(values, showInactive);
    const isLabTypes = key === "labTypes";
    const isMaintenanceTypes = key === "maintenanceTypes";
    const isStructuredStreamTypes = isLabTypes || isMaintenanceTypes;
    return `
        <div>
            <div class="settings-type-toolbar ${isStructuredStreamTypes ? "settings-type-toolbar--structured" : ""}">
                <label class="block font-medium">${title}</label>
                ${renderTypeSettingsCheckbox({
                    checked: showInactive,
                    dataAttrs: ` data-type-show-inactive data-key="${attrValue(key)}"`,
                    label: "Show inactive",
                })}
            </div>
            ${isLabTypes ? renderLabTypeColumnHeaders() : ""}
            ${isMaintenanceTypes ? renderMaintenanceTypeColumnHeaders() : ""}
            <div class="space-y-2" data-key="${key}">
                ${visibleValues.map((value) => {
        const originalIndex = tempConfigData?.[key]?.findIndex(
            (item) => item && item.id === value.id,
        );
        if (originalIndex === undefined || originalIndex === -1) {return "";}

        const isActive = value.isActive !== false;
        const sameStateValues = visibleValues.filter((item) =>
            (item?.isActive !== false) === isActive);
        const stateIndex = sameStateValues.findIndex((item) => item?.id === value.id);
        const isFirst = stateIndex === 0;
        const isLast = stateIndex === sameStateValues.length - 1;
        const inactiveAttr = isActive ? "" : " disabled";
        const mainFieldCount = Array.isArray(value.fields)
            ? value.fields.length
            : 0;
        const requiredFieldCount = Array.isArray(value.repairDynamicFields)
            ? value.repairDynamicFields.length
            : 0;
        const rowShellClasses = [
            isLabTypes ? "settings-lab-type-row" : "",
            isMaintenanceTypes ? "settings-maintenance-type-row" : "",
        ].filter(Boolean).join(" ");

        return `
            <div class="settings-type-row-shell ${rowShellClasses} ${!isActive ? "opacity-75" : ""}">
                <div class="settings-config-row settings-type-row">
                    <div class="settings-config-order-controls" aria-label="Type order controls">
                        ${renderTypeOrderButton({key, index: originalIndex, direction: "up", disabled: !isActive || isFirst})}
                        ${renderTypeOrderButton({key, index: originalIndex, direction: "down", disabled: !isActive || isLast})}
                    </div>
                    <div class="settings-type-row-main">
                        <div class="settings-config-primary">
                            <input type="text" value="${attrValue(value.name || "")}" data-index="${originalIndex}" data-prop="name" class="settings-config-name settings-config-name--colored" style="${getColorInputStyle(value.color)}" placeholder="Type Name"${inactiveAttr}>
                        </div>
                        ${renderTypeColorControl({
                            key,
                            index: originalIndex,
                            color: value.color,
                            disabled: !isActive,
                        })}
                        <div class="settings-type-team-control">
                            <div data-type-team-control data-key="${attrValue(key)}" data-index="${originalIndex}"></div>
                        </div>
                        ${isStructuredStreamTypes ? "" : renderTypeStreamScopeControls(
                            key,
                            originalIndex,
                            value,
                            !isActive,
                        )}
                        ${isStructuredStreamTypes ? "" : `
                            <button type="button" class="manage-fields-btn settings-secondary-row-btn" data-index="${originalIndex}" data-key="${key}" data-field-key="fields"${inactiveAttr}>
                                Manage Fields
                            </button>
                        `}
                    </div>
                </div>
                ${isLabTypes ? `
                    ${renderLabTypeStreamCells(key, originalIndex, value, !isActive)}
                    <div class="settings-maintenance-type-field-cell" data-mobile-label="Main Fields">
                        ${renderMaintenanceFieldButton({
                            key,
                            index: originalIndex,
                            fieldKey: "fields",
                            label: "Main Fields",
                            count: mainFieldCount,
                            disabled: !isActive,
                        })}
                    </div>
                ` : ""}
                ${isMaintenanceTypes ? `
                    ${renderMaintenanceTypeStreamCells(key, originalIndex, value, !isActive)}
                    <div class="settings-maintenance-type-field-cell" data-mobile-label="Main Fields">
                        ${renderMaintenanceFieldButton({
                            key,
                            index: originalIndex,
                            fieldKey: "fields",
                            label: "Main Fields",
                            count: mainFieldCount,
                            disabled: !isActive,
                        })}
                    </div>
                    <div class="settings-maintenance-type-field-cell" data-mobile-label="Repair Fields">
                        ${typeSupportsMaintenanceIssueStream(key, value) ? renderMaintenanceFieldButton({
                            key,
                            index: originalIndex,
                            fieldKey: "repairDynamicFields",
                            label: "Repair Fields",
                            count: requiredFieldCount,
                            disabled: !isActive,
                        }) : '<span class="settings-maintenance-type-field-empty">Issue only</span>'}
                    </div>
                ` : ""}
                <div class="settings-option-boolean-cell">
                    <button type="button" class="archive-type-btn settings-config-archive ${isActive ? "" : "settings-config-restore"}" data-index="${originalIndex}" data-key="${attrValue(key)}" title="${isActive ? "Archive type" : "Restore type"}" aria-label="${isActive ? "Archive type" : "Restore type"}">
                        <span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span>
                    </button>
                </div>
                <div class="settings-config-action-cell">
                    <button type="button" data-index="${originalIndex}" data-key="${key}" class="delete-type-btn settings-config-delete"><span class="material-symbols-outlined text-lg">delete</span></button>
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-type-btn settings-add-row-btn mt-3" data-key="${key}">+ Add Type</button>
        </div>
    `;
}


/**
 * Closes open type-team scope dropdowns when clicking outside them.
 * @param {MouseEvent} event Click event.
 * @returns {void}
 */
export function closeTypeSettingsDropdownsOnOutsideClick(event) {
    if (event.target.closest("[data-type-team-control]")) {return;}
}

/**
 * Applies type-team scope checkbox changes.
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler dependencies.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @returns {boolean} True when the change was handled.
 */
export function handleTypeSettingsChange(
    target,
    {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = {},
) {
    if (target.matches("[data-type-show-inactive]")) {
        const key = target.dataset.key;
        if (key) {showInactiveTypesByKey.set(key, target.checked);}
        rerenderCurrentTabContent();
        return true;
    }

    if (target.matches("[data-type-team-control]")) {
        updateSaveButton(true);
        return true;
    }

    if (!target.matches(".type-team-scope-check") || target.dataset.prop !== "teamIds") {
        return false;
    }

    const container = target.closest("[data-key]");
    const key = container?.dataset.key;
    const index = parseInt(target.dataset.index, 10);
    const item = tempConfigData?.[key]?.[index];
    if (!item || typeof item !== "object") {return false;}

    let nextTeamIds = Array.isArray(item.teamIds) ? [...item.teamIds] : [];
    if (target.checked) {
        if (!nextTeamIds.includes(target.value)) {
            nextTeamIds.push(target.value);
        }
    } else {
        nextTeamIds = nextTeamIds.filter((teamId) =>
            teamId !== target.value);
    }

    item.teamIds = nextTeamIds;
    const dropdownContainer = target.closest(
        ".relative-dropdown-container",
    );
    const btn = dropdownContainer?.querySelector(".type-team-dropdown-btn");
    const btnText = btn?.querySelector(".btn-text");
    const label = nextTeamIds.length > 0
        ? `${nextTeamIds.length} ${nextTeamIds.length === 1 ? "Team" : "Teams"} Selected`
        : "All Teams";

    if (btnText) {btnText.textContent = label;}
    if (btn) {
        if (nextTeamIds.length > 0) {
            btn.classList.remove(
                "text-gray-400",
                "border-gray-200",
                "dashed",
            );
            btn.classList.add("text-gray-900", "border-gray-300");
        } else {
            btn.classList.add(
                "text-gray-400",
                "border-gray-200",
                "dashed",
            );
            btn.classList.remove("text-gray-900", "border-gray-300");
        }
    }

    updateSaveButton(true);
    return true;
}

/**
 * Handles type settings buttons and field-editor actions.
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler dependencies.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {Promise<boolean>} True when the click was handled.
 */
export async function handleTypeSettingsButtonClick(target, options = {}) {
    const {
        updateSaveButton = () => {},
        rerenderCurrentTabContent = () => {},
    } = options;

    if (target.closest(".move-type-btn")) {
        const btn = target.closest(".move-type-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);
        if (moveTypeItem(key, index, btn.dataset.direction)) {
            updateSaveButton(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (target.closest(".add-type-btn")) {
        const btn = target.closest(".add-type-btn");
        const key = btn.dataset.key;
        if (!tempConfigData[key]) {tempConfigData[key] = [];}

        const maxOrder = tempConfigData[key].length > 0
            ? Math.max(0, ...tempConfigData[key].map((item) => item.order || 0))
            : 0;

        const newType = {
            id: `type_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            name: "New Type",
            color: "#3B82F6",
            order: maxOrder + 1,
            isActive: true,
            teamIds: [],
            fields: [],
        };
        if (key === "labTypes" || key === "maintenanceTypes") {
            newType.streams = [];
        }
        if (key === "maintenanceTypes") {
            newType.repairDynamicFields = [];
        }
        tempConfigData[key].push(newType);

        updateSaveButton(true);
        rerenderCurrentTabContent();
        requestTypeNameFocus(key, newType.id);
        return true;
    }

    if (target.closest(".archive-type-btn")) {
        const btn = target.closest(".archive-type-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);
        const typeItem = tempConfigData?.[key]?.[index];
        if (!typeItem || typeof typeItem !== "object") {return true;}

        const willRestore = typeItem.isActive === false;
        if (willRestore) {
            typeItem.isActive = true;
            typeItem.order = getMaxActiveTypeOrder(key) + 1;
        } else {
            typeItem.isActive = false;
        }
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".delete-type-btn")) {
        const btn = target.closest(".delete-type-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);
        const typeToDelete = tempConfigData[key][index];

        const checkTypeMap = {
            taskTypes: "task_type",
            patientTypes: "patient_type",
            projectTypes: "project_type",
            assetTypes: "asset_type",
            labTypes: "lab_type",
            maintenanceTypes: "maintenance_type",
        };
        const checkType = checkTypeMap[key];

        const originalIcon = btn.innerHTML;
        btn.innerHTML = '<span class="material-symbols-outlined text-lg animate-spin">progress_activity</span>';
        btn.disabled = true;

        let usageCount = 0;
        if (checkType) {
            usageCount = await checkDataUsage(checkType, typeToDelete.id);
        }

        btn.innerHTML = originalIcon;
        btn.disabled = false;

        if (usageCount > 0) {
            openConfirmationModal(
                "Cannot Delete Type",
                `This type is currently assigned to <strong>${usageCount} items</strong>.<br><br>Please use the <strong>Data Migration Tool</strong> (in General Settings) to move these items to another type before deleting.`,
                () => {},
                false,
                "Okay",
            );
            return true;
        }

        openConfirmationModal(
            "Delete Type?",
            "Are you sure you want to delete this type? This cannot be undone.",
            () => {
                tempConfigData[key].splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    if (target.closest(".manage-fields-btn")) {
        const btn = target.closest(".manage-fields-btn");
        renderFieldEditorModal(
            btn.dataset.key,
            parseInt(btn.dataset.index, 10),
            updateSaveButton,
            rerenderCurrentTabContent,
            btn.dataset.fieldKey || "fields",
        );
        return true;
    }


    return false;
}

function renderFieldEditorModal(
    typeKey,
    typeIndex,
    updateSaveButton,
    rerenderCurrentTabContent,
    fieldKey = "fields",
) {
    const typeItem = tempConfigData?.[typeKey]?.[typeIndex];
    if (!typeItem) {return;}
    if (!Array.isArray(typeItem[fieldKey])) {typeItem[fieldKey] = [];}
    const fields = typeItem[fieldKey];
    const title = fieldKey === "repairDynamicFields"
        ? "Repair fields"
        : "Custom fields";

    const modalHtml = `
        <div class="settings-field-modal-backdrop" id="field-editor-modal">
            <div class="settings-field-modal settings-control-surface transform transition-all duration-300 scale-95 opacity-0" id="field-editor-content">
                <div class="settings-field-modal-header">
                    <div class="min-w-0">
                        <h3 class="text-lg font-semibold text-gray-900">${title}</h3>
                        <p class="mt-0.5 truncate text-sm text-gray-500">${escapeHTML(typeItem.name || "Untitled Type")}</p>
                    </div>
                </div>
                <div class="settings-field-modal-body">
                    <div id="fields-list-container" class="settings-field-list">
                        ${renderFieldsList(fields)}
                    </div>
                    <button type="button" id="add-field-btn" class="settings-field-add-btn settings-add-row-btn">
                        <span class="material-symbols-outlined text-base">add</span>
                        <span>Add field</span>
                    </button>
                </div>
                <div class="settings-field-modal-footer">
                    <button type="button" id="cancel-field-editor" class="settings-field-modal-cancel-btn">
                        Cancel
                    </button>
                    <button type="button" id="save-fields-btn" class="settings-field-modal-done-btn">
                        Done
                    </button>
                </div>
            </div>
        </div>
    `;

    document.getElementById("field-editor-modal")?.remove();
    document.body.insertAdjacentHTML("beforeend", modalHtml);
    const fieldEditorContent = document.getElementById("field-editor-content");
    if (fieldEditorContent) {
        applyBrandingVariables(fieldEditorContent, appData);
        bindNativeFieldBehavior(fieldEditorContent);
    }
    setTimeout(() => {
        const content = document.getElementById("field-editor-content");
        content?.classList.remove("scale-95", "opacity-0");
        content?.classList.add("scale-100", "opacity-100");
    }, 10);

    const close = () => {
        document.getElementById("field-editor-modal")?.remove();
        rerenderCurrentTabContent();
    };

    document.getElementById("cancel-field-editor")?.addEventListener("click", close);
    document.getElementById("save-fields-btn")?.addEventListener("click", close);
    document.getElementById("add-field-btn")?.addEventListener("click", () => {
        addField(fields);
        refreshFieldsList(fields, updateSaveButton);
        updateSaveButton(true);
    });

    const fieldsContainer = document.getElementById("fields-list-container");
    if (!fieldsContainer) {return;}
    bindFieldEditorControls(fields, updateSaveButton);

    fieldsContainer.addEventListener("click", (event) => {
        const row = event.target.closest(".field-row");
        if (!row) {return;}
        const index = parseInt(row.dataset.index, 10);

        if (event.target.closest(".delete-field-btn")) {
            fields.splice(index, 1);
            refreshFieldsList(fields, updateSaveButton);
            updateSaveButton(true);
            return;
        }

        if (event.target.closest(".move-up-field-btn") && index > 0) {
            [fields[index - 1], fields[index]] = [
                fields[index],
                fields[index - 1],
            ];
            refreshFieldsList(fields, updateSaveButton);
            updateSaveButton(true);
            return;
        }

        if (
            event.target.closest(".move-down-field-btn") &&
            index < fields.length - 1
        ) {
            [fields[index + 1], fields[index]] = [
                fields[index],
                fields[index + 1],
            ];
            refreshFieldsList(fields, updateSaveButton);
            updateSaveButton(true);
        }
    });

    fieldsContainer.addEventListener("change", (event) => {
        const row = event.target.closest(".field-row");
        if (!row) {return;}
        const index = parseInt(row.dataset.index, 10);
        const field = fields[index];
        const prop = event.target.dataset.prop;
        if (!prop) {return;}

        field[prop] = event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value;
        updateSaveButton(true);

        if (prop === "type") {
            refreshFieldsList(fields, updateSaveButton);
        }
    });
}

function renderFieldsList(fields) {
    if (!fields || fields.length === 0) {
        return '<div class="settings-field-empty">No custom fields yet.</div>';
    }

    const rows = fields.map((field, index) => {
        const placeholderDisabled = isFieldPlaceholderDisabled(field);
        const requiredAvailable = isFieldRequiredAvailable(field);
        return `
        <div class="field-row settings-field-row" data-index="${index}">
            <div class="settings-config-order-controls settings-field-order-controls" aria-label="Field order controls">
                ${renderFieldOrderButton({direction: "up", disabled: index === 0})}
                ${renderFieldOrderButton({direction: "down", disabled: index === fields.length - 1})}
            </div>

            <div class="settings-field-row-main">
                <div class="settings-field-control settings-field-control--type" data-mobile-label="Input type">
                    <div class="settings-field-type-control" data-field-type-control data-index="${index}"></div>
                </div>
                <label class="settings-field-control settings-field-control--label" data-mobile-label="Label">
                    <input type="text" value="${attrValue(field.label || "")}" data-prop="label" class="settings-field-input" placeholder="e.g. Procedure Date" aria-label="Field label">
                </label>
                ${placeholderDisabled ? `
                    <span class="settings-field-empty-cell" aria-hidden="true"></span>
                ` : `
                    <label class="settings-field-control settings-field-control--placeholder" data-mobile-label="Placeholder text">
                        <input type="text" value="${attrValue(field.placeholder || "")}" data-prop="placeholder" class="settings-field-input" placeholder="Help text" aria-label="Placeholder text">
                    </label>
                `}
            </div>

            <div class="settings-field-required-cell ${requiredAvailable ? "" : "settings-field-required-cell--empty"}" ${requiredAvailable ? 'data-mobile-label="Required"' : ""}>
                ${requiredAvailable ? `
                    ${renderTypeSettingsCheckbox({
                        checked: field.required,
                        dataAttrs: ' data-prop="required"',
                        label: "",
                        title: "Required",
                    })}
                ` : '<span aria-hidden="true"></span>'}
            </div>

            <div class="settings-config-action-cell">
                <button type="button" class="delete-field-btn settings-config-delete" title="Delete field" aria-label="Delete field">
                    <span class="material-symbols-outlined text-lg">delete</span>
                </button>
            </div>
        </div>
    `;
    }).join("");

    return `
        <div class="settings-field-header" aria-hidden="true">
            <span></span>
            <div class="settings-field-header-main">
                <span>Input type</span>
                <span>Label</span>
                <span>Placeholder text</span>
            </div>
            <span class="text-center">Required</span>
            <span></span>
        </div>
        ${rows}
    `;
}

function refreshFieldsList(fields, updateSaveButton = () => {}) {
    const container = document.getElementById("fields-list-container");
    if (container) {
        container.innerHTML = renderFieldsList(fields);
        bindFieldEditorControls(fields, updateSaveButton);
    }
}

function addField(fieldsArray) {
    fieldsArray.push({
        id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        label: "New Field",
        type: "text",
        required: false,
        placeholder: "",
        order: fieldsArray.length,
    });
}
